import React from "react";

import { AccountAlRlPills } from "./account-al-rl-pills";
import {
  getAccountCoverageCardTooltip,
  getAccountCoverageStats,
  getAccountIdentityBadgeStyle,
  getAccountIdentityColor,
  getInitials,
  getPersonalAccountCardStats,
  getPersonalCoveragePercent,
} from "./relationshipUtils";

export { getPersonalCoveragePercent };

function AccountCardHeader({ account }) {
  return (
    <div className="t3-hcard-hdr">
      <div
        className="t3-hcard-logo"
        style={getAccountIdentityBadgeStyle(account.id)}
        aria-hidden
      >
        {getInitials(account.name)}
      </div>
      <div className="t3-hcard-name" title={account.name}>
        {account.name}
      </div>
    </div>
  );
}

function AccountRelationshipsCardContent({ account }) {
  const cov = getAccountCoverageStats(account);
  const subTxt = cov.mapped
    ? `${cov.assessed} assessed · ${cov.assessedPct}%`
    : cov.total
      ? "Not yet mapped"
      : "—";
  const barTitle = getAccountCoverageCardTooltip(account, cov);

  return (
    <>
      <AccountCardHeader account={account} />
      <div className="acct-picker-card-pills">
        <AccountAlRlPills account={account} layout="row" />
      </div>
      <div className="card-meta">
        {cov.mapped}/{cov.total} pairs mapped
      </div>
      <div className="card-meta-sub">{subTxt}</div>
      <div className="card-bar" title={barTitle} aria-hidden>
        <div className="card-bar-fill" style={{ width: `${cov.mappedPct}%` }} />
        <div
          className="card-bar-inner"
          style={{ width: `${cov.overallPct}%` }}
        />
      </div>
    </>
  );
}

function MyRelationshipsCardContent({ account, youLabel }) {
  const stats = getPersonalAccountCardStats(account, { youLabel });
  const identityColor = getAccountIdentityColor(account.id);

  return (
    <>
      <AccountCardHeader account={account} />
      <div className="acct-picker-card-pills">
        <AccountAlRlPills account={account} layout="row" />
      </div>
      <div className="t3-hcard-progress">
        <div className="t3-rl-bar" aria-hidden>
          <span
            style={{ width: `${stats.barG}%`, background: "var(--green)" }}
          />
          <span
            style={{ width: `${stats.barA}%`, background: "var(--amber)" }}
          />
          <span style={{ width: `${stats.barR}%`, background: "var(--red)" }} />
        </div>
        <span className="t3-hcard-pct" style={{ color: identityColor }}>
          {stats.pct}%
        </span>
      </div>
      <div className="t3-hcard-foot">
        <span className="t3-hcard-foot-label">{stats.youLabel}:</span>{" "}
        {stats.assessedYouCt}/{stats.mappedCt}
        <span className="t3-hcard-foot-sep" aria-hidden>
          ·
        </span>
        <span className="t3-hcard-foot-label">Acct:</span>{" "}
        {stats.accountAssessedCt}/{stats.allAccountContacts}
      </div>
    </>
  );
}

export function AccountPickerCard({
  account,
  variant = "account-relationships",
  isSelected = false,
  isPeek = false,
  isCompareSelected = false,
  disabled = false,
  onSelect,
  onClick,
  onHoverAccount,
  youLabel = "You",
}) {
  const isActive = isSelected || isPeek || isCompareSelected;

  const handleClick = event => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (onClick) {
      onClick(event);
      return;
    }
    onSelect?.(account.id);
  };

  const className = [
    "acct-picker-card",
    variant === "my-relationships"
      ? "acct-picker-card--my-relationships"
      : "acct-picker-card--account-relationships",
    isSelected ? "is-selected" : "",
    isPeek ? "is-peek" : "",
    isCompareSelected ? "is-compare-pick" : "",
    disabled ? "is-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const stats =
    variant === "my-relationships"
      ? getPersonalAccountCardStats(account, { youLabel })
      : null;
  const cov =
    variant === "account-relationships"
      ? getAccountCoverageStats(account)
      : null;
  const title =
    variant === "my-relationships"
      ? account?.name
        ? `${account.name}\n${stats.tooltipTitle}`
        : stats.tooltipTitle
      : getAccountCoverageCardTooltip(account, cov);

  return (
    <button
      type="button"
      data-acct-card={account.id}
      className={className}
      onClick={handleClick}
      disabled={disabled}
      onMouseEnter={
        onHoverAccount && !disabled
          ? () => onHoverAccount(account.id)
          : undefined
      }
      onMouseLeave={
        onHoverAccount && !disabled ? () => onHoverAccount(null) : undefined
      }
      title={
        disabled
          ? `${account?.name || "Account"} — only shared team accounts are available while viewing the teammate graph`
          : title
      }
      aria-pressed={isActive}
      aria-disabled={disabled ? "true" : undefined}
      style={
        disabled
          ? {
              opacity: 0.45,
              cursor: "not-allowed",
              filter: "grayscale(0.4)",
              pointerEvents: "auto",
            }
          : undefined
      }
    >
      {isSelected && variant === "account-relationships" ? (
        <span className="acct-picker-card-accent" aria-hidden />
      ) : null}
      {variant === "my-relationships" ? (
        <MyRelationshipsCardContent account={account} youLabel={youLabel} />
      ) : (
        <AccountRelationshipsCardContent account={account} />
      )}
    </button>
  );
}
